import { useConfig, type CWElementsConfig } from '@cw-elements/config';
import { CROPWISE_MFES_PATH, initMfeDynamicSlot } from '@cw-elements/mfe-dynamic-slot';
import { lazyRoute } from './lazy-route';
import { Spin } from 'antd';
import MainLayout from 'components/layout.component';
import RouteByPermissionFlag from 'infra/routes/by-permission-flag.route';
import NemaDigital from 'infra/routes/nema-digital.route';
import NemaDigitalMapView from 'infra/routes/nemadigital-map-view.route';
import NemaDigitalMapViewWorkspace from 'infra/routes/nemadigital-map-view-workspace.route';
import Notes from 'infra/routes/notes.route';
import { BorerRiskRoute, LegacyRoutingRedirect, PestManagementSectionRoute } from 'infra/routes/pest-management.route';
import ProtectedRoute from 'infra/routes/protected.route';
import FieldDetailsRoute from 'infra/routes/field-details.route';
import ReportsList from 'infra/routes/reports-list.route';
import CustomForms from 'infra/routes/custom-forms.route';
import VisitDashboard from 'infra/routes/visit-dashboard.route';
import SmartPlan from 'infra/routes/smart-plan.route';
import { DEFAULT_REGIONAL_OVERVIEW_MODEL } from '@features/regional-overview';
import LoginOauth2 from 'pages/login/login.oauth2.container';
import { PestManagementSections } from 'pages/sugarcane/pest-management.routes';
import { useRedirectDeckOfApp } from 'pages/redirect/redirect.hooks';
import { Suspense, useEffect } from 'react';
import { Routes } from 'react-router';
import { Navigate, Route, useNavigate } from 'react-router-dom';
import { createPlanKeyedWrapper } from './scouting-plan-keyed-routes';
import { tasksRoutes } from './tasks.routes';
import { organizationFarmRoutes } from './organization-farm.routes';
import { RiskManagementShell } from 'pages/regional-overview-v2/risk-management-shell';
import { ScoutingPlanEntryRoute } from 'pages/routes-configuration/v2/scouting-plan-v2.route';

const MfeDynamicSlot = lazyRoute(() => import('@cw-elements/mfe-dynamic-slot'));

const HallOfCompanies = lazyRoute(() => import('pages/hall-of-companies/hall-of-companies.container'));
const CompanyEmptyState = lazyRoute(() => import('pages/hall-of-companies/hall-of-companies-empty-state'));
const HallOfProperties = lazyRoute(() => import('pages/dashboard/dashboard.container'));
const PropertyDashboard = lazyRoute(() => import('pages/dashboard-farm/dashboard-farm.container'));
const CompanyPresentation = lazyRoute(() => import('pages/presentation-mode/presentation-mode.container'));
const CompanyWarehouse = lazyRoute(() => import('pages/warehouse/company-warehouse.container'));

const OrganizationCreatePage = lazyRoute(() => import('pages/organization-create/organization-create-page'));
const OrganizationEditPage = lazyRoute(() => import('pages/organization-edit/organization-edit-page'));
const ProductDetailsPage = lazyRoute(() => import('pages/product-details/product-details-page'));

const AreaInfo = lazyRoute(() => import('pages/timeline/area-info/area-info'));
const Timeline = lazyRoute(() => import('pages/timeline/timeline.page.container'));
const TimelineTable = lazyRoute(() => import('pages/timeline/timeline-table-v2.page'));

const FixedPoints = lazyRoute(() => import('pages/fixed-points/fixed-points.container'));
const StaticPoints = lazyRoute(() => import('pages/static-points/static-points.container'));
const ScoutingPlanVisualization = lazyRoute(() => import('pages/scouting-plan-visualization/scouting-plan-visualization.container'));
const RoutesConfiguration = lazyRoute(() => import('pages/routes-configuration/routes-configuration.container'));
const RoutingConfigurationV2Route = lazyRoute(() => import('pages/routes-configuration/v2/routing-configuration-v2.route'));
const ScoutingPlanV2Route = lazyRoute(() => import('pages/routes-configuration/v2/scouting-plan-v2.route'));
const FieldBookContainer = lazyRoute(() => import('pages/field-book/field-book.container'));

const ScoutingScoreHallContainer = lazyRoute(() => import('pages/scouting-score/scouting-score-hall.container'));
const ScoutingScoreCreateFormContainer = lazyRoute(() => import('pages/scouting-score/scouting-score-create-form.container'));

const DiseaseRisk = lazyRoute(() => import('pages/disease-risk/disease-risk.container'));
const DiseaseRiskConfiguration = lazyRoute(() => import('pages/disease-risk/configuration/configuration.container'));
const DiseaseRiskCropCalendar = lazyRoute(() => import('pages/disease-risk/crop-calendar/crop-calendar'));
const DiseaseRiskShellContainer = lazyRoute(() => import('pages/disease-risk-v2/disease-risk-shell.container'));
const DiseaseRiskByPropertyContainer = lazyRoute(() => import('pages/disease-risk-v2/disease-risk-by-property.container'));
const DiseaseRiskCropCalendarContainer = lazyRoute(() => import('pages/disease-risk-v2/disease-risk-crop-calendar.container'));
const DiseaseRiskConfigurationContainer = lazyRoute(() => import('pages/disease-risk-v2/disease-risk-configuration.container'));

const CottonGrowth = lazyRoute(() => import('pages/cotton/cotton.container'));

const PostHarvestMapGuard = lazyRoute(() => import('pages/post-harvest-map/post-harvest-map.guard'));

const FieldClusteringConfiguration = lazyRoute(() => import('pages/field-clustering/configuration/field-clustering-configuration'));
const FieldClusteringMapResult = lazyRoute(() => import('pages/field-clustering/map-result/field-clustering-map-result'));
const FieldClusteringMapVisualization = lazyRoute(
  () => import('pages/field-clustering/map-visualization/field-clustering-map-visualization')
);

const AgOperationsCreateEmbeddedContainer = lazyRoute(
  () => import('pages/ag-operations/ag-operations-create-mfe/ag-operations-create-embedded.container')
);
const AgOperationsListEmbeddedContainer = lazyRoute(
  () => import('pages/ag-operations/ag-operations-list-mfe/ag-operations-list-embedded.container')
);

const SmartSpray = lazyRoute(() => import('pages/smart-spray/smart-spray.container'));
const NewSmartSprayNewPlan = lazyRoute(() => import('pages/smart-spray/plan/plan.container'));

const CreateControlStrategyContainer = lazyRoute(
  () => import('pages/control-strategy/create-control-strategy/create-control-strategy.container')
);
const PriorityConfigurationContainer = lazyRoute(() => import('pages/control-strategy/features/priority-configuration'));

const EditMethodology = lazyRoute(() => import('pages/edit-methodology/pages/edit-methodology.container'));
const EditMethodologyList = lazyRoute(() => import('pages/edit-methodology/pages/edit-methodology-list.container'));

const RegionalOverviewContainer = lazyRoute(() => import('pages/regional-overview-v2/regional-overview.adapter'));
const TrapManagementContainer = lazyRoute(() => import('pages/regional-overview-v2/trap-management.adapter'));

const ManagementOverviewContainer = lazyRoute(() => import('@features/management-overview/management-overview.container'));

const OpportunityMachineContainer = lazyRoute(() => import('pages/opportunity-machine/dashboard/opportunity-machine.container'));
const OpportunityMachineSetupContainer = lazyRoute(() => import('pages/opportunity-machine/setup/opportunity-machine-setup.container'));

const PowerBIReports = lazyRoute(() => import('pages/powerbi-reports/powerbi-reports-container'));

const Integrations = lazyRoute(() => import('pages/integrations/integrations.container'));
const IntegrationsPerfectFlightContainer = lazyRoute(
  () => import('pages/integrations/perfect-flight/integrations-perfect-flight-container')
);
const IntegrationsPerfectFlightInstall = lazyRoute(() => import('pages/integrations/perfect-flight/integrations-perfect-flight-install'));
const IntegrationsPerfectFlightLinkingFarmComponent = lazyRoute(
  () => import('pages/integrations/perfect-flight/integrations-perfect-flight-linking-farm-component')
);
const IntegrationsPerfectFlightLinkingArea = lazyRoute(
  () => import('pages/integrations/perfect-flight/integrations-perfect-flight-linking-area.component')
);
const IntegrationsJdocContainer = lazyRoute(() => import('pages/integrations/jdoc/integrations-jdoc-container'));
const IntegrationsJDOCInstall = lazyRoute(() => import('pages/integrations/jdoc/integrations-jdoc-install.component'));
const IntegrationsJDOCLogin = lazyRoute(() => import('pages/integrations/jdoc/integrations-jdoc-login.component'));
const IntegrationsJDOCLinkingProperty = lazyRoute(() => import('pages/integrations/jdoc/integrations-jdoc-linking-property.component'));
const IntegrationsJDOCLinkingArea = lazyRoute(() => import('pages/integrations/jdoc/integration-jdoc-linking-area.component'));
const IntegrationsRea = lazyRoute(() => import('pages/integrations/rea/integrations-rea'));
const SetupIntegration = lazyRoute(() => import('pages/integrations/rea/SetupIntegration'));
const ViewSyncedData = lazyRoute(() => import('pages/integrations/rea/ViewSyncedData'));
const IntegrationsTrapViewInstall = lazyRoute(() => import('pages/integrations/trap-view/integrations-trapview-install'));
const IntegrationsSaa = lazyRoute(() => import('pages/integrations/saa/integrations-saa'));

const SubscriptionManagementContainer = lazyRoute(() => import('pages/subscription-management/subscription-management.container'));
const SubscriptionManagementEmbeddedContainer = lazyRoute(
  () => import('pages/subscription-management/subscription-management-embedded.container')
);
const SubscribeMyselfEmbeddedContainer = lazyRoute(() => import('pages/subscribe-myself/subscribe-myself-embedded.container'));

const AiChatbotEmbeddedContainer = lazyRoute(() => import('pages/ai-chatbot/ai-chatbot-embedded.container'));

const UnlicensedUserContainer = lazyRoute(() => import('pages/login/login.unlicensed-user'));
const RedirectComponent = lazyRoute(() => import('pages/redirect/redirect.container'));
const NotFound = lazyRoute(() => import('./not-found'));

const ScoutingPlanVisualizationKeyed = createPlanKeyedWrapper(ScoutingPlanVisualization);
const PostHarvestMapKeyed = createPlanKeyedWrapper(PostHarvestMapGuard);

const LocalRoutes = () => {
  const navigate = useNavigate();

  useEffect(() => {
    initMfeDynamicSlot(navigate);
  }, [navigate]);

  useRedirectDeckOfApp();

  return (
    <Routes>
      <Route path='*' element={<NotFound />} />
      <Route path='authenticate' element={<LoginOauth2 />} />
      <Route element={<ProtectedRoute />}>
        <Route path='/' element={<MainLayout />}>
          <Route path='/no-companies' element={<CompanyEmptyState />} />
          <Route path='/redirect' element={<RedirectComponent />} />
          <Route path='/unlicensed' element={<UnlicensedUserContainer />} />
          <Route path='/product-details/:productId' element={<ProductDetailsPage />} />
          <Route path='/workspace/:workspaceId/organization-create' element={<OrganizationCreatePage />} />
          <Route path='/workspace/:workspaceId/organization-edit/:organizationId' element={<OrganizationEditPage />} />

          <Route path={organizationFarmRoutes.smartPlan.organization} element={<SmartPlan />} />
          <Route path={organizationFarmRoutes.smartPlan.property} element={<SmartPlan routeLevel='property' />} />

          <Route path='/company/:companyId/overview' element={<ManagementOverviewContainer />} />
          <Route path={'/hall-of-companies/*'} element={<HallOfCompanies />} />
          <Route path='/company/:companyId' element={<HallOfProperties />} />
          <Route path='/company/:companyId/presentation' element={<CompanyPresentation />} />
          <Route path='/company/:companyId/edit-methodology' element={<EditMethodologyList />} />
          <Route path='/company/:companyId/edit-methodology/:methodologyId' element={<EditMethodology />} />
          <Route element={<RouteByPermissionFlag permissionFlag='hideWarehouse' reverseCondition />}>
            <Route path={organizationFarmRoutes.warehouses.organization} element={<CompanyWarehouse />} />
          </Route>
          {['/company/:companyId/regional-overview', '/risk-management/:companyId'].map(path => (
            <Route key={path} path={path} element={<RiskManagementShell />}>
              <Route index element={<Navigate to={DEFAULT_REGIONAL_OVERVIEW_MODEL} replace />} />
              <Route path=':model' element={<RegionalOverviewContainer />} />
              <Route path=':model/trap-management/:selectedTab' element={<TrapManagementContainer />} />
            </Route>
          ))}
          <Route path={organizationFarmRoutes.reports.organization} element={<PowerBIReports />} />
          <Route path='/workspace/:workspaceId/reports' element={<PowerBIReports />} />
          <Route path={organizationFarmRoutes.reportsList.organization} element={<ReportsList />} />
          <Route path='/workspace/:workspaceId/reports-list' element={<ReportsList />} />
          <Route path='/company/:companyId/visit-dashboard' element={<VisitDashboard />} />
          <Route path='/workspace/:workspaceId/visit-dashboard' element={<VisitDashboard />} />

          <Route element={<RouteByPermissionFlag permissionFlag='hidePropertyDashboard' reverseCondition />}>
            <Route path='/company/:companyId/property/:propertyId' element={<PropertyDashboard />} />
          </Route>
          <Route path={organizationFarmRoutes.reportsList.property} element={<ReportsList />} />
          <Route element={<RouteByPermissionFlag permissionFlag='hideWarehouse' reverseCondition />}>
            <Route path={organizationFarmRoutes.warehouses.property} element={<CompanyWarehouse />} />
          </Route>
          <Route path='/company/:companyId/property/:propertyId/area-info' element={<AreaInfo />} />
          <Route path='/company/:companyId/property/:propertyId/timeline' element={<Timeline />} />
          <Route path='/company/:companyId/property/:propertyId/timeline/table' element={<Timeline />} />
          <Route path='/company/:companyId/property/:propertyId/timeline/table-v2' element={<TimelineTable />} />
          <Route path='/company/:companyId/property/:propertyId/timeline/field/:fieldId' element={<FieldDetailsRoute />} />
          <Route path='/company/:companyId/property/:propertyId/custom-forms/:formId' element={<CustomForms />} />

          <Route
            path='/company/:companyId/property/:propertyId/disease-risk'
            element={
              <Suspense fallback={<Spin fullscreen />}>
                <DiseaseRisk />
              </Suspense>
            }
          />
          <Route
            path='/company/:companyId/property/:propertyId/disease-risk-by-property'
            element={
              <Suspense fallback={<Spin fullscreen />}>
                <DiseaseRiskShellContainer />
              </Suspense>
            }>
            <Route
              index
              element={
                <Suspense fallback={<Spin fullscreen />}>
                  <DiseaseRiskByPropertyContainer />
                </Suspense>
              }
            />
            <Route
              path='crop-calendar'
              element={
                <Suspense fallback={<Spin fullscreen />}>
                  <DiseaseRiskCropCalendarContainer />
                </Suspense>
              }
            />
            <Route
              path='configuration'
              element={
                <Suspense fallback={<Spin fullscreen />}>
                  <DiseaseRiskConfigurationContainer />
                </Suspense>
              }
            />
          </Route>
          <Route path='/company/:companyId/property/:propertyId/disease-risk/configuration' element={<DiseaseRiskConfiguration />} />
          <Route path='/company/:companyId/property/:propertyId/disease-risk/crop-calendar' element={<DiseaseRiskCropCalendar />} />

          <Route path='/company/:companyId/property/:propertyId/fixed-points' element={<FixedPoints />} />
          <Route element={<RouteByPermissionFlag permissionFlag='hideTrapsManagement' reverseCondition />}>
            <Route path='/company/:companyId/property/:propertyId/static-points' element={<StaticPoints />} />
          </Route>
          <Route
            path='/company/:companyId/property/:propertyId/static-points/scouting-plan/map'
            element={<ScoutingPlanVisualizationKeyed />}
          />
          {tasksRoutes}
          <Route element={<RouteByPermissionFlag permissionFlag='hideSmartSpray' reverseCondition />}>
            <Route path='/company/:companyId/property/:propertyId/smart-spray' element={<SmartSpray />} />
          </Route>
          <Route element={<RouteByPermissionFlag permissionFlag='hideSmartSpray' reverseCondition />}>
            <Route path='/company/:companyId/property/:propertyId/smart-spray/new-plan' element={<NewSmartSprayNewPlan />} />
          </Route>
          <Route path='/company/:companyId/property/:propertyId/cotton' element={<CottonGrowth />} />
          <Route element={<RouteByPermissionFlag permissionFlag='hideIntegrations' reverseCondition />}>
            <Route path='/company/:companyId/property/:propertyId/integrations' element={<Integrations />} />
          </Route>
          <Route element={<RouteByPermissionFlag permissionFlag='hideIntegrations' reverseCondition />}>
            <Route
              path='/company/:companyId/property/:propertyId/integrations/perfect-flight'
              element={<IntegrationsPerfectFlightContainer />}>
              <Route path='install' element={<IntegrationsPerfectFlightInstall />} />
              <Route path='linking-property' element={<IntegrationsPerfectFlightLinkingFarmComponent />} />
              <Route path='linking-areas' element={<IntegrationsPerfectFlightLinkingArea />} />
            </Route>
          </Route>
          <Route element={<RouteByPermissionFlag permissionFlag='hideIntegrations' reverseCondition />}>
            <Route path='/company/:companyId/property/:propertyId/integrations/jdoc' element={<IntegrationsJdocContainer />}>
              <Route path='install' element={<IntegrationsJDOCInstall />} />
              <Route path='login' element={<IntegrationsJDOCLogin />} />
              <Route path='linking-property' element={<IntegrationsJDOCLinkingProperty />} />
              <Route path='linking-areas/org/:orgId/farm/:farmId/' element={<IntegrationsJDOCLinkingArea />} />
            </Route>
          </Route>
          <Route path='/company/:companyId/property/:propertyId/integrations/trap-view' element={<IntegrationsTrapViewInstall />} />
          <Route path='/company/:companyId/property/:propertyId/integrations/saa' element={<IntegrationsSaa />} />
          <Route path='/company/:companyId/property/:propertyId/integrations/rea' element={<IntegrationsRea />} />
          <Route path='/company/:companyId/property/:propertyId/integrations/rea/view-synced-data' element={<ViewSyncedData />} />
          <Route path='/company/:companyId/property/:propertyId/integrations/rea/rea-integration-setup' element={<SetupIntegration />} />

          <Route path='/company/:companyId/property/:propertyId/borer-risk' element={<BorerRiskRoute />} />

          <Route
            path='/company/:companyId/property/:propertyId/pest-management/risk'
            element={<PestManagementSectionRoute section={PestManagementSections.RISK} />}
          />
          <Route
            path='/company/:companyId/property/:propertyId/pest-management/control-strategy'
            element={<PestManagementSectionRoute section={PestManagementSections.CONTROL_STRATEGY} />}
          />
          <Route path='/company/:companyId/property/:propertyId/pest-management/routing' element={<ScoutingPlanEntryRoute />} />
          <Route path='/company/:companyId/property/:propertyId/v2/pest-management/routing' element={<ScoutingPlanV2Route />} />
          <Route
            path='/company/:companyId/property/:propertyId/pest-management/control-strategy/priority'
            element={
              <Suspense fallback={<Spin fullscreen />}>
                <PriorityConfigurationContainer />
              </Suspense>
            }
          />
          <Route path='/company/:companyId/property/:propertyId/pest-management/routing/configuration' element={<RoutesConfiguration />} />
          <Route
            path='/company/:companyId/property/:propertyId/v2/pest-management/routing/configuration'
            element={<RoutingConfigurationV2Route />}
          />
          <Route path='/company/:companyId/property/:propertyId/pest-management/routing/map' element={<PostHarvestMapKeyed />} />

          <Route
            path='/company/:companyId/property/:propertyId/borer-risk/new-strategy'
            element={
              <Suspense fallback={<Spin fullscreen />}>
                <CreateControlStrategyContainer />
              </Suspense>
            }
          />
          <Route
            path='/company/:companyId/property/:propertyId/pest-management/control-strategy/new-strategy'
            element={
              <Suspense fallback={<Spin fullscreen />}>
                <CreateControlStrategyContainer />
              </Suspense>
            }
          />
          <Route path={organizationFarmRoutes.reports.property} element={<PowerBIReports />} />
          <Route path='/company/:companyId/property/:propertyId/scouting-score' element={<ScoutingScoreHallContainer />} />
          <Route
            path='/company/:companyId/property/:propertyId/scouting-score/configuration'
            element={<ScoutingScoreCreateFormContainer />}>
            <Route path=':configurationId' element={<ScoutingScoreCreateFormContainer />} />
          </Route>

          <Route path='/company/:companyId/property/:propertyId/integration/:typeIntegration'>
            <Route
              path='disease-risk'
              element={
                <Suspense fallback={<Spin fullscreen />}>
                  <DiseaseRisk />
                </Suspense>
              }
            />
          </Route>

          <Route
            path='/company/:companyId/property/:propertyId/integration/:typeIntegration/disease-risk/configuration'
            element={<DiseaseRiskConfiguration />}
          />

          <Route path='/company/:companyId/field-book' element={<FieldBookContainer />} />

          <Route
            path='/company/:companyId/property/:propertyId/tasks/ScoutingPlanPage'
            element={<LegacyRoutingRedirect section={PestManagementSections.ROUTING} />}
          />
          <Route
            path='/company/:companyId/property/:propertyId/tasks/routesConfiguration'
            element={<LegacyRoutingRedirect section={PestManagementSections.ROUTING_CONFIGURATION} />}
          />
          <Route
            path='/company/:companyId/property/:propertyId/tasks/postHarvest/map'
            element={<LegacyRoutingRedirect section={PestManagementSections.ROUTING_MAP} />}
          />

          <Route path='/company/:companyId/property/:propertyId/notes' element={<Notes />} />

          <Route path={organizationFarmRoutes.nemaDigital.organization} element={<NemaDigital />} />
          <Route path={organizationFarmRoutes.nemaDigital.property} element={<NemaDigital />} />
          <Route path='/company/:companyId/property/:propertyId/nemadigital/map-view' element={<NemaDigitalMapView />} />
          <Route path='/workspace/:workspaceId/nemadigital' element={<NemaDigitalMapViewWorkspace />} />
          <Route path='/workspace/:workspaceId/nemadigital/my-region' element={<NemaDigitalMapViewWorkspace />} />
          <Route path='/workspace/:workspaceId/nemadigital/map-view/*' element={<NemaDigitalMapViewWorkspace />} />

          <Route path='/company/:companyId/opportunity-machine' element={<OpportunityMachineContainer />} />
          <Route path='/company/:companyId/opportunity-machine/setup' element={<OpportunityMachineSetupContainer />} />

          <Route
            path='/company/:companyId/property/:propertyId/field-clustering/configuration'
            element={<FieldClusteringConfiguration />}
          />
          <Route path='/company/:companyId/property/:propertyId/field-clustering/map/' element={<FieldClusteringMapResult />} />
          <Route path='/company/:companyId/property/:propertyId/field-clustering' element={<FieldClusteringMapVisualization />} />
          <Route
            path={CROPWISE_MFES_PATH}
            element={<MfeDynamicSlot getConfigInstance={useConfig as () => CWElementsConfig} onCancel={() => navigate(-1)} />}
          />
          <Route path='/workspace/:workspaceId/subscription-management' element={<SubscriptionManagementContainer />} />
          <Route
            path='/workspace/:workspaceId/company/:companyId/userId/:userId/subscription-management-embedded'
            element={<SubscriptionManagementEmbeddedContainer />}
          />
          <Route path='/workspace/:workspaceId/subscription-management-embedded' element={<SubscriptionManagementEmbeddedContainer />} />
          <Route path='/workspace/:workspaceId/subscribe-myself-embedded' element={<SubscribeMyselfEmbeddedContainer />} />
          <Route
            path='/company/:companyId/property/:propertyId/ag-operation-create-embedded'
            element={<AgOperationsCreateEmbeddedContainer />}
          />
          <Route
            path='/company/:companyId/property/:propertyId/ag-operation-list-embedded'
            element={<AgOperationsListEmbeddedContainer />}
          />
          <Route path='/workspace/:workspaceId/ai-chatbot-embedded' element={<AiChatbotEmbeddedContainer />} />
          <Route path='/workspace/:workspaceId/company/:companyId/ai-chatbot-embedded' element={<AiChatbotEmbeddedContainer />} />
          <Route
            path='/workspace/:workspaceId/company/:companyId/property/:propertyId/ai-chatbot-embedded'
            element={<AiChatbotEmbeddedContainer />}
          />
          <Route
            path='/workspace/:workspaceId/company/:companyId/property/:propertyId/field/:fieldId/ai-chatbot-embedded'
            element={<AiChatbotEmbeddedContainer />}
          />
          <Route path={organizationFarmRoutes.subscriptionManagement.organization} element={<SubscriptionManagementContainer />} />
          <Route path={organizationFarmRoutes.subscriptionManagement.property} element={<SubscriptionManagementContainer />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default LocalRoutes;
